/*-
 * *****
 * Concord
 * -----
 * Copyright (C) 2017 - 2018 Walmart Inc.
 * -----
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =====
 */

import * as React from 'react';
import {
    Dispatch,
    MutableRefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Header, Modal } from 'semantic-ui-react';

import { ConcordId, RequestError } from '../../../api/common';
import { LogSegment } from '../../molecules';
import {
    getFullSegmentLog,
    getSegmentLog as apiGetLog,
    LogRange,
    LogTooLargeError,
    SegmentStatus,
} from '../../../api/process/log';
import RequestErrorActivity from '../RequestErrorActivity';
import { LogProcessorOptions, processText } from '../../../state/data/processes/logs/processors';
import { isFinal, ProcessStatus } from '../../../api/process';
import { TaskCallDetails } from '../index';

import './styles.css';

const DATA_FETCH_INTERVAL = 5000;
const DEFAULT_RANGE: LogRange = { low: undefined, high: 2048 };
const MAX_COPY_SIZE_BYTES = 5 * 1024 * 1024; // copying huge logs freezes the tab and is rarely useful

interface ExternalProps {
    instanceId: ConcordId;
    segmentId: number;
    correlationId?: string;
    name: string;
    createdAt: string;
    processStatus?: ProcessStatus;
    status?: SegmentStatus;
    statusUpdatedAt?: string;
    warnings?: number;
    errors?: number;
    opts: LogProcessorOptions;
    forceRefresh: boolean;
    forceOpen: boolean;
}

interface FetchResponse {
    data: string;
    range: LogRange;
}

const copyTextToClipboard = async (text: string): Promise<boolean> => {
    // deliberately no execCommand()/prompt() fallback here: this call happens after an
    // await (the log fetch), so by the time we get here the browser may have already
    // revoked "user activation" — falling back to the execCommand-based library in that
    // state doesn't help (it needs activation too) and its own failure path is a
    // window.prompt() containing the entire (possibly multi-MB) log text, which is worse
    // than just reporting the copy as failed
    if (!navigator.clipboard || !window.isSecureContext) {
        return false;
    }
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

const LogSegmentActivity = ({
    instanceId,
    processStatus,
    segmentId,
    correlationId,
    name,
    createdAt,
    status,
    statusUpdatedAt,
    warnings,
    errors,
    opts,
    forceRefresh,
    forceOpen,
}: ExternalProps) => {
    const range = useRef<LogRange>(DEFAULT_RANGE);
    const rangeInit = useRef<LogRange>(DEFAULT_RANGE);

    const [refresh, setRefresh] = useState<boolean>(false);
    const [stopPolling, setStopPolling] = useState<boolean>(true);
    const [data, setData] = useState<string[]>([]);
    const [loadedRange, setLoadedRange] = useState<LogRange>({});
    const [visibleData, setVisibleData] = useState<string[]>([]);
    const [segmentInfoOpen, setSegmentInfoOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [copying, setCopying] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [copyFailed, setCopyFailed] = useState<boolean>(false);
    const [copyTooLarge, setCopyTooLarge] = useState<boolean>(false);
    const copyFeedbackTimeout = useRef<number | undefined>(undefined);
    const [continueFetch, setContinueFetch] = useState<boolean>(true);

    const fetchData = useCallback(
        async (range: LogRange) => {
            const chunk = await apiGetLog(instanceId, segmentId, range);

            const data = chunk && chunk.data.length > 0 ? chunk.data : undefined;
            const processedData = data ? processText(data, opts) : '';

            return {
                data: processedData,
                range: chunk.range,
            };
        },
        [instanceId, segmentId, opts]
    );

    const startPollingHandler = useCallback((isLoadWholeLog: boolean) => {
        if (isLoadWholeLog) {
            range.current = { low: 0 };
            rangeInit.current = { low: 0 };
        } else {
            range.current = DEFAULT_RANGE;
            rangeInit.current = DEFAULT_RANGE;
        }

        setData([]);
        setStopPolling(false);
        setRefresh((prevState) => !prevState);
    }, []);

    const stopPollingHandler = useCallback(() => {
        setData([]);
        setStopPolling(true);
    }, []);

    const segmentInfoHandler = useCallback(() => {
        setSegmentInfoOpen(true);
    }, []);

    const showCopyFeedback = useCallback((result: 'copied' | 'failed' | 'too-large') => {
        setCopied(result === 'copied');
        setCopyFailed(result === 'failed');
        setCopyTooLarge(result === 'too-large');
        if (copyFeedbackTimeout.current) {
            window.clearTimeout(copyFeedbackTimeout.current);
        }
        copyFeedbackTimeout.current = window.setTimeout(
            () => {
                setCopied(false);
                setCopyFailed(false);
                setCopyTooLarge(false);
            },
            result === 'too-large' ? 3000 : 1500
        );
    }, []);

    const copyLogHandler = useCallback(async () => {
        setCopying(true);
        try {
            const log = await getFullSegmentLog(instanceId, segmentId, MAX_COPY_SIZE_BYTES);
            const ok = await copyTextToClipboard(log);
            showCopyFeedback(ok ? 'copied' : 'failed');
        } catch (e) {
            showCopyFeedback(e instanceof LogTooLargeError ? 'too-large' : 'failed');
        } finally {
            setCopying(false);
        }
    }, [instanceId, segmentId, showCopyFeedback]);

    useEffect(() => {
        return () => {
            if (copyFeedbackTimeout.current) {
                window.clearTimeout(copyFeedbackTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        setRefresh((prevState) => !prevState);
    }, [forceRefresh]);

    useEffect(() => {
        range.current = rangeInit.current;
        setData([]);
    }, [opts]);

    useEffect(() => {
        if (data.length > 0) {
            setVisibleData(data);
        }
    }, [data]);

    useEffect(() => {
        const isFinalOrSuspended =
            isFinal(processStatus) || processStatus === ProcessStatus.SUSPENDED;
        const isFinalSegmentStatus = status !== undefined && status !== SegmentStatus.RUNNING;
        setContinueFetch(!isFinalOrSuspended && !isFinalSegmentStatus);
    }, [processStatus, status]);

    const error = usePolling(
        fetchData,
        range,
        setData,
        setLoadedRange,
        setLoading,
        refresh,
        stopPolling,
        continueFetch
    );

    if (error) {
        return <RequestErrorActivity error={error} />;
    }

    return (
        <>
            <LogSegment
                instanceId={instanceId}
                segmentId={segmentId}
                processStatus={processStatus}
                name={name}
                createdAt={createdAt}
                status={status}
                statusUpdatedAt={statusUpdatedAt}
                warnings={warnings}
                errors={errors}
                onStartLoading={startPollingHandler}
                onStopLoading={stopPollingHandler}
                onSegmentInfo={correlationId ? segmentInfoHandler : undefined}
                onCopy={copyLogHandler}
                copying={copying}
                copied={copied}
                copyFailed={copyFailed}
                copyTooLarge={copyTooLarge}
                data={visibleData}
                lowRange={loadedRange.low}
                loading={loading}
                forceOpen={forceOpen}
            />

            {correlationId && (
                <Modal
                    open={segmentInfoOpen}
                    onClose={() => setSegmentInfoOpen(false)}
                    size="small"
                >
                    <Header icon="browser" content={name} />
                    <Modal.Content scrolling={true}>
                        <TaskCallDetails instanceId={instanceId} correlationId={correlationId} />
                    </Modal.Content>
                </Modal>
            )}
        </>
    );
};

const usePolling = (
    request: (range: LogRange) => Promise<FetchResponse>,
    rangeRef: MutableRefObject<LogRange>,
    setData: Dispatch<SetStateAction<string[]>>,
    setRange: Dispatch<SetStateAction<LogRange>>,
    setLoading: Dispatch<SetStateAction<boolean>>,
    refresh: boolean,
    stopPollingIndicator: boolean,
    continueFetch?: boolean
): RequestError | undefined => {
    const poll = useRef<number | undefined>(undefined);
    const [error, setError] = useState<RequestError>();

    useEffect(() => {
        let cancelled = false;

        const fetchData = async (range: LogRange) => {
            let r = range;
            try {
                setLoading(true);
                const resp = await request(r);

                r = { low: resp.range.high, high: undefined };

                setError(undefined);

                if (!cancelled && resp.data.length > 0) {
                    rangeRef.current = r;
                    setData((prev) => [...prev, resp.data]);
                    setRange((prev) => {
                        // was a full load or a first tail load
                        if (range.low === 0 || range.high !== undefined) {
                            return {
                                low: resp.range.low,
                                high: resp.range.high,
                                length: resp.range.length,
                            };
                        }

                        return prev;
                    });
                }
            } catch (e) {
                setError(e);
            } finally {
                setLoading(false);

                if (!stopPollingIndicator && !cancelled && continueFetch) {
                    poll.current = window.setTimeout(() => fetchData(r), DATA_FETCH_INTERVAL);
                } else {
                    stopPolling();
                }
            }
        };

        if (stopPollingIndicator) {
            return;
        }

        fetchData(rangeRef.current);

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [
        request,
        setData,
        setRange,
        rangeRef,
        setLoading,
        refresh,
        stopPollingIndicator,
        continueFetch,
    ]);

    const stopPolling = () => {
        if (poll.current) {
            clearTimeout(poll.current);
            poll.current = undefined;
        }
    };

    return error;
};

export default LogSegmentActivity;
